import type {
  AncestorsBreadcrumbDataOptions,
  AncestorsBreadcrumbItem,
  GetAncestorBreadcrumbItemProps,
} from "./ancestors-breadcrumb.type";
import type { BreadcrumbItem } from "@/modules/breadcrumb";
import type { ModelKey } from "@/modules/model";

import { getModelLink } from "@/modules/model";

export const getAncestorsBreadcrumbItems = (
  ancestors?: AncestorsBreadcrumbItem[],
  options?: AncestorsBreadcrumbDataOptions
) => {
  if (!ancestors?.length) return [];

  const { exclude, include, keepLast } = options ?? {};

  if (!keepLast) {
    ancestors = ancestors.slice(0, -1);
  }

  if (exclude?.length) {
    ancestors = ancestors.filter((a) => !exclude.includes(a.type));
  } else if (include?.length) {
    ancestors = ancestors.filter((a) => include.includes(a.type));
  }

  const firstLocationIndex = ancestors.findIndex(
    (ancestor) => ancestor.type === "location"
  );

  return ancestors.map((ancestor, ancestorIndex) =>
    getAncestorBreadcrumbItem({ ancestor, ancestorIndex, firstLocationIndex })
  );
};

export const getAncestorBreadcrumbItem = ({
  ancestor,
  ancestorIndex,
  firstLocationIndex,
}: GetAncestorBreadcrumbItemProps): BreadcrumbItem => {
  const modelKey = getAncestorBreadcrumbItemModelKey({
    ancestor,
    ancestorIndex,
    firstLocationIndex,
  });

  const { name, queryParams } = ancestor;

  let link = getModelLink(modelKey, ancestor.id);
  if (queryParams) link += queryParams;

  return {
    title: name,
    href: link,
    model: modelKey,
    ...getAncestorBreadcrumbItemStateProps(ancestor),
  };
};

export const getAncestorBreadcrumbItemStateProps = (
  ancestor: AncestorsBreadcrumbItem
): Pick<BreadcrumbItem, "state" | "hideIcon"> => {
  if (!ancestor.showState || !("state" in ancestor)) return {};

  return {
    state: {
      type: ancestor.type,
      value: ancestor.state,
    },
    hideIcon: true,
  };
};

export const getAncestorBreadcrumbItemModelKey = ({
  ancestor,
  ancestorIndex,
  firstLocationIndex,
}: GetAncestorBreadcrumbItemProps): ModelKey => {
  if (ancestor.type !== "location") return ancestor.type;

  return ancestorIndex !== firstLocationIndex ? "sector" : "location";
};
